import { useEffect, useState } from 'react'
import type { FormEvent } from 'react'
import { Loader2, PiggyBank } from 'lucide-react'
import { supabase } from '@/integrations/supabase/client'
import { useLanguage } from '@/context/LanguageContext'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

interface DonationSettingsDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  userId: string
}

const inputClass =
  'w-full rounded-xl border border-neutral-200 bg-white px-3 py-2.5 text-sm text-neutral-900 outline-none transition focus:border-neutral-900 focus:ring-2 focus:ring-neutral-900/10'

export default function DonationSettingsDialog({
  open,
  onOpenChange,
  userId,
}: DonationSettingsDialogProps) {
  const { language } = useLanguage()
  const [paypalMe, setPaypalMe] = useState('')
  const [donationLink, setDonationLink] = useState('')
  const [message, setMessage] = useState('')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)

  useEffect(() => {
    if (!open || !userId) return
    setLoading(true)
    setError(null)
    setSaved(false)
    supabase
      .from('profiles')
      .select('donation_paypal_email, donation_link, donation_message')
      .eq('id', userId)
      .single()
      .then(({ data, error: err }) => {
        setLoading(false)
        if (err) {
          setError(err.message)
          return
        }
        const row = data as any
        const stored = row?.donation_paypal_email ?? ''
        if (stored && /^https?:\/\//i.test(stored)) {
          setPaypalMe(stored)
        } else if (stored) {
          setPaypalMe(`https://www.paypal.me/${stored}`)
        } else {
          setPaypalMe('')
        }
        setDonationLink(row?.donation_link ?? '')
        setMessage(row?.donation_message ?? '')
      })
  }, [open, userId])

  const save = async (e: FormEvent) => {
    e.preventDefault()
    if (saving) return
    setSaving(true)
    setError(null)
    setSaved(false)
    const hasDonationInfo = paypalMe.trim() || donationLink.trim()
    const { error: err } = await supabase
      .from('profiles')
      .update({
        donation_paypal_email: paypalMe.trim() || null,
        donation_link: donationLink.trim() || null,
        donation_message: message.trim() || null,
        donation_enabled: !!hasDonationInfo,
      })
      .eq('id', userId)
    setSaving(false)
    if (err) {
      setError(err.message)
      return
    }
    setSaved(true)
    setTimeout(() => onOpenChange(false), 1200)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-lg max-w-md max-h-[90vh] overflow-y-auto glass-strong">
        <DialogHeader>
          <DialogTitle className="text-xl font-extrabold text-neutral-900 flex items-center gap-2">
            <PiggyBank className="h-5 w-5" style={{ color: '#EC4899' }} />
            {language === 'en' ? 'Piggy bank' : 'Salvadanaio'}
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-neutral-400" />
          </div>
        ) : (
          <form onSubmit={save} className="space-y-4">
            <p className="text-sm text-neutral-500">
              {language === 'en'
                ? 'Allow anyone visiting your wishlist to contribute with a donation. Fill in at least one of the fields below.'
                : 'Permetti a chi visita la tua wishlist di contribuire con una donazione. Compila almeno uno dei campi seguenti.'}
            </p>

            <div>
              <label className="mb-1 block text-sm font-semibold text-slate-800">
                {language === 'en' ? 'PayPal.me link' : 'Link PayPal.me'}
              </label>
              <input
                type="url"
                value={paypalMe}
                onChange={(e) => setPaypalMe(e.target.value)}
                placeholder="https://www.paypal.me/tuonome"
                className={inputClass}
              />
              <p className="mt-1 text-xs text-neutral-400">
                {language === 'en'
                  ? 'Anyone who wants to donate will be redirected to your PayPal.me.'
                  : 'Chi vuole donare verrà reindirizzato al tuo PayPal.me.'}
              </p>
            </div>

            <div>
              <label className="mb-1 block text-sm font-semibold text-slate-800">
                {language === 'en' ? 'Alternative donation link' : 'Link di donazione alternativo'}
              </label>
              <input
                type="url"
                value={donationLink}
                onChange={(e) => setDonationLink(e.target.value)}
                placeholder="https://..."
                className={inputClass}
              />
              <p className="mt-1 text-xs text-neutral-400">
                {language === 'en' ? 'e.g. Ko-fi, Buy Me a Coffee, etc.' : 'Es. Ko-fi, Buy Me a Coffee, ecc.'}
              </p>
            </div>

            <div>
              <label className="mb-1 block text-sm font-semibold text-slate-800">
                {language === 'en' ? 'Custom message' : 'Messaggio personalizzato'}
              </label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={6}
                maxLength={200}
                placeholder={
                  language === 'en'
                    ? 'e.g. Would you rather surprise me? You can contribute here!'
                    : 'Es. Preferisci farmi una sorpresa? Puoi contribuire qui!'
                }
                className={inputClass}
              />
              <p className="mt-1 text-right text-xs text-neutral-400">
                {message.length}/200
              </p>
            </div>

            {error && <p className="text-sm text-red-600">{error}</p>}
            {saved && (
              <p className="text-sm text-green-600 font-semibold">
                {language === 'en' ? 'Settings saved!' : 'Impostazioni salvate!'}
              </p>
            )}

            <button
              type="submit"
              disabled={saving}
              className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-neutral-900 px-5 py-2.5 text-sm font-medium text-white transition hover:bg-neutral-800 disabled:opacity-50"
            >
              {saving && <Loader2 className="h-4 w-4 animate-spin" />}
              {saving
                ? (language === 'en' ? 'Saving...' : 'Salvataggio...')
                : (language === 'en' ? 'Save settings' : 'Salva impostazioni')}
            </button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  )
}
